import React from 'react';
import { PlusIcon, TrashIcon, StarIcon } from '@heroicons/react/24/outline';
import { StarIcon as StarSolidIcon } from '@heroicons/react/24/solid';
import type { ClientSignatory } from '../types';

interface SignatoryManagerProps {
  signatories: Omit<ClientSignatory, 'id' | 'created_at' | 'updated_at' | 'client'>[];
  onChange: (signatories: Omit<ClientSignatory, 'id' | 'created_at' | 'updated_at' | 'client'>[]) => void;
}

const SignatoryManager: React.FC<SignatoryManagerProps> = ({ signatories, onChange }) => {
  const addSignatory = () => {
    onChange([
      ...signatories,
      {
        name: '',
        email: '',
        phone: '',
        document: '',
        role: '',
        is_primary: signatories.length === 0,
        is_active: true,
        observations: '',
      },
    ]);
  };

  const removeSignatory = (index: number) => {
    const newSignatories = signatories.filter((_, i) => i !== index);
    // Se remover o principal e tem outros, marca o primeiro como principal
    if (signatories[index].is_primary && newSignatories.length > 0) {
      newSignatories[0].is_primary = true;
    }
    onChange(newSignatories);
  };

  const updateSignatory = (index: number, field: string, value: any) => {
    const newSignatories = [...signatories];
    newSignatories[index] = { ...newSignatories[index], [field]: value };

    // Se marcou como principal, desmarcar outros
    if (field === 'is_primary' && value === true) {
      newSignatories.forEach((sig, i) => {
        if (i !== index) sig.is_primary = false;
      });
    }

    onChange(newSignatories);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-sm font-medium text-gray-900">Signatários</h3>
        <button
          type="button"
          onClick={addSignatory}
          className="text-xs px-3 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 flex items-center"
        >
          <PlusIcon className="h-4 w-4 mr-1" />
          Adicionar Signatário
        </button>
      </div>

      {signatories.length === 0 ? (
        <p className="text-xs text-gray-500 italic">Nenhum signatário adicionado</p>
      ) : (
        <div className="space-y-4">
          {signatories.map((signatory, index) => (
            <div key={index} className="border border-gray-300 rounded-md p-3 bg-gray-50">
              <div className="flex justify-between items-start mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-gray-700">Signatário #{index + 1}</span>
                  {signatory.is_primary && (
                    <span className="text-xs px-2 py-0.5 bg-yellow-100 text-yellow-800 rounded flex items-center">
                      <StarSolidIcon className="h-3 w-3 mr-1" />
                      Principal
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => removeSignatory(index)}
                  className="text-red-600 hover:text-red-900"
                  title="Remover"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Nome Completo *
                  </label>
                  <input
                    type="text"
                    value={signatory.name}
                    onChange={(e) => updateSignatory(index, 'name', e.target.value)}
                    className="w-full px-2 py-1 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Email *
                  </label>
                  <input
                    type="email"
                    value={signatory.email}
                    onChange={(e) => updateSignatory(index, 'email', e.target.value)}
                    className="w-full px-2 py-1 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Telefone
                  </label>
                  <input
                    type="tel"
                    value={signatory.phone || ''}
                    onChange={(e) => updateSignatory(index, 'phone', e.target.value)}
                    className="w-full px-2 py-1 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    CPF
                  </label>
                  <input
                    type="text"
                    value={signatory.document || ''}
                    onChange={(e) => updateSignatory(index, 'document', e.target.value)}
                    className="w-full px-2 py-1 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="000.000.000-00"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Cargo/Função
                  </label>
                  <input
                    type="text"
                    value={signatory.role || ''}
                    onChange={(e) => updateSignatory(index, 'role', e.target.value)}
                    className="w-full px-2 py-1 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Ex: Diretor, Gerente, etc."
                  />
                </div>

                <div className="flex items-center gap-2">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={signatory.is_primary}
                      onChange={(e) => updateSignatory(index, 'is_primary', e.target.checked)}
                      className="mr-2"
                    />
                    <span className="text-xs text-gray-700 flex items-center">
                      <StarIcon className="h-3 w-3 mr-1" />
                      Signatário Principal
                    </span>
                  </label>

                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={signatory.is_active}
                      onChange={(e) => updateSignatory(index, 'is_active', e.target.checked)}
                      className="mr-2"
                    />
                    <span className="text-xs text-gray-700">Ativo</span>
                  </label>
                </div>
              </div>

              <div className="mt-3">
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Observações
                </label>
                <textarea
                  value={signatory.observations || ''}
                  onChange={(e) => updateSignatory(index, 'observations', e.target.value)}
                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={2}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SignatoryManager;
